import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Link,
  CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { forgetpassword } from "../redux/slices/user/thunk";
import CustomTextField from "../components/RTLTextField";
import { useNavigate } from "react-router-dom";

const ForgotPasswordScreen = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language == "ar";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState("email");
  const [loading, setLoading] = useState(false);

  // const handleLoginMethodChange = (event, newMethod) => {
  //   if (newMethod !== null) {
  //     setLoginMethod(newMethod);
  //     formik.setFieldValue('emailOrPhone', '');
  //   }
  // };

  // Form validation schema
  const validationSchema = Yup.object({
    emailOrPhone:
      loginMethod === "email"
        ? Yup.string().email(t("email_invalid")).required(t("email_required"))
        : Yup.string()
            // .matches(/^[0-9]+$/, t("phone_only_numbers"))
            // .min(10, t("phone_too_short"))
            // .max(15, t("phone_too_long"))
            .required(t("phone_required")),
  });

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      emailOrPhone: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true)
        let data = {
          email: values.emailOrPhone,
        };
        const response = await dispatch(forgetpassword({ data }));
        if (response.payload) {
          navigate("/setpassword", {
            state: {
              user: response?.payload?.data,
            },
          });
        }
        console.log("response", response.payload.data);
      } catch (error) {
        console.log("error", error);
        
      }finally{
        setLoading(false)
      }
      // console.log('Password recovery submitted:', values);
      // Add your password recovery logic here
    },
  });

  let loginImage;
  try {
    loginImage = require("../assets/Logo.png");
  } catch (error) {
    loginImage = null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid
        container
        spacing={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: 3,
          border: "1px solid #d0d0d0",
        }}
      >
        {/* Left Column - Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: theme.palette.primary.mainLoginBackground,
            py: 6,
            px: { xs: 3, md: 6 },
          }}
        >
          <Box sx={{ maxWidth: 500, mx: "auto" }}>
            {/* Back to Login Link */}
            <Link
              href="/login"
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 3,
                color: theme.palette.blackandWhite.main,
                textDecoration: "none",
                fontWeight: 500,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <ArrowBack sx={{ mr: 1 }} />
              {t("Back to login")}
            </Link>

            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: theme.palette.blackandWhite.main,
              }}
            >
              {t("Forgot your password?")}
            </Typography>

            {/* <Typography variant="body1" sx={{ 
              mb: 4,
              color: theme.palette.text.secondary
            }}>
              {t("recovery_message")}
            </Typography> */}

            {/* Email/Phone Toggle
              <ToggleButtonGroup
             value={loginMethod}
             exclusive
             onChange={handleLoginMethodChange}
             sx={{
               display: 'flex',
               justifyContent: 'center',
               mb: 3,
               '& .MuiToggleButton-root': {
                 py: 0.5,
                 px: 4,
                 fontSize: '0.75rem',
                 textTransform: 'none',
               //   borderRadius: '6px',
                 color: '#333',
               //   border: '1px solid #ccc',
                 '&.Mui-selected': {
                   backgroundColor: '#fff',
                   color: '#1976d2', // لون النص عند الاختيار (تقدر تغيره)
                   fontWeight: 600,
                   // borderColor: '#1976d2',
                   '&:hover': {
                     backgroundColor: '#f5f5f5',
                   },
                 },
               },
             }}
           >
             <ToggleButton value="email" sx={{px:2}}>{t("Email")}</ToggleButton>
             <ToggleButton value="phone" sx={{px:2}}>{t("Phone")}</ToggleButton>
           </ToggleButtonGroup> */}

            {/* Recovery Form */}
            <form onSubmit={formik.handleSubmit}>
              {/* Email/Phone Field */}
              <CustomTextField
                fullWidth
                id="emailOrPhone"
                name="emailOrPhone"
                label={
                  loginMethod === "email"
                    ? t("Email address")
                    : t("Phone number")
                }
                value={formik.values.emailOrPhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.emailOrPhone &&
                  Boolean(formik.errors.emailOrPhone)
                }
                helperText={
                  formik.touched.emailOrPhone && formik.errors.emailOrPhone
                }
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                }}
                isRtl={isRtl}
              />

              {/* Submit Button */}
              <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{
                  py: 1.5,
                  mb: 3,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: "8px",
                }}
                disabled={loading}
              >
                { loading ? <CircularProgress /> : t("submit")}
              </Button>
            </form>
          </Box>
        </Grid>

        {/* Right Column - Image */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.23)",
          }}
        >
          {loginImage ? (
            <img
              src={loginImage}
              alt="Login"
              style={{
                width: "50%",
                // height: '100%',
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {t("Login Image Placeholder")}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default ForgotPasswordScreen;
